import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  QuestionMarkCircleIcon,
  ChevronRightIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  DocumentTextIcon,
  PhotoIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const Help = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [selectedHelp, setSelectedHelp] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showFilter, setShowFilter] = useState(false);

  const getTargetAudienceLabel = (target) => {
    if (!target) return '전체';
    
    const targets = target.split(',');
    const labels = targets.map(t => {
      switch (t.trim()) {
        case 'all': return '전체';
        case 'contractor': return '소장';
        case 'customer': return '고객';
        case 'vip_customer': return 'VIP 고객';
        default: return t.trim();
      }
    });
    
    return labels.join(', ');
  };

  const getTargetAudienceColor = (target) => {
    if (!target) return 'bg-gray-100 text-gray-800';
    
    const targets = target.split(',');
    
    if (targets.includes('all')) {
      return 'bg-blue-100 text-blue-800';
    } else if (targets.length > 1) {
      return 'bg-indigo-100 text-indigo-800';
    } else {
      switch (targets[0].trim()) {
        case 'contractor': return 'bg-purple-100 text-purple-800';
        case 'customer': return 'bg-green-100 text-green-800';
        case 'vip_customer': return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
  };

  // 유튜브 스타일 삽입
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* 일반 유튜브 영상 (16:9) */
      .youtube-video-container {
        position: relative;
        width: 100%;
        max-width: 800px;
        margin: 2rem auto;
        padding-bottom: 56.25%; /* 16:9 비율 */
        height: 0;
        overflow: hidden;
        background: #000;
        border-radius: 0.5rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }
      
      .youtube-video-container iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
      
      /* 유튜브 쇼츠 (9:16) */
      .youtube-shorts-container {
        position: relative;
        width: 100%;
        max-width: 450px;
        margin: 2rem auto;
        padding-bottom: 177.78%; /* 9:16 비율 */
        height: 0;
        overflow: hidden;
        background: #000;
        border-radius: 0.5rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }
      
      .youtube-shorts-container iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
      
      /* 모바일에서 쇼츠 크기 조정 */
      @media (max-width: 640px) {
        .youtube-shorts-container {
          max-width: 300px;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { data: helpData, isLoading } = useQuery(
    ['helps', page, search, category],
    () => api.get('/helps', {
      params: {
        page,
        limit: 10,
        search: search || undefined,
        category: category !== 'all' ? category : undefined
      }
    }).then(res => res.data),
    {
      enabled: !!user
    }
  );

  const handleHelpClick = async (help) => {
    try {
      const response = await api.get(`/helps/${help.id}`);
      setSelectedHelp(response.data);
    } catch (error) {
      console.error('도움말 상세 정보 조회 실패:', error);
      setSelectedHelp(help);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'yyyy.MM.dd', { locale: ko });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'start': '시작하기',
      'feature': '기능 안내',
      'project': '프로젝트 안내',
      'vip': 'VIP 안내',
      'troubleshoot': '문제 해결'
    };
    return labels[category] || category;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">도움말을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-xl">
                <QuestionMarkCircleIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  도움말
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  자주 묻는 질문과 사용 가이드
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-3 py-1.5 bg-gray-100 rounded-lg">
                <span className="text-sm font-medium text-gray-900">{helpData?.total || 0}개</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 검색 및 필터 */}
        <div className="mb-6 space-y-4">
          {/* 검색창 */}
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="도움말 검색..."
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </form>

          {/* 필터 영역 */}
          {(showFilter || window.innerWidth >= 640) && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setCategory('all'); setPage(1); }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  category === 'all'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                전체
              </button>
              {helpData?.categories?.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setCategory(cat); setPage(1); }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    category === cat
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {getCategoryLabel(cat)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 모바일 필터 버튼 및 전체 개수 */}
        <div className="sm:hidden mb-4 flex items-center gap-2">
          <button
            onClick={() => setShowFilter(!showFilter)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm"
          >
            <FunnelIcon className="h-5 w-5 text-gray-600" />
            <span className="font-medium">필터 {showFilter ? '닫기' : '열기'}</span>
          </button>
          <div className="bg-gray-100 px-3 py-2 rounded-xl">
            <span className="text-xs text-gray-600">전체</span>
            <span className="ml-1 text-sm font-semibold text-gray-900">{helpData?.total || 0}</span>
          </div>
        </div>

        {/* 도움말 목록 */}
        <div className="space-y-4">
          {helpData?.helps?.map((help) => (
            <div
              key={help.id}
              onClick={() => handleHelpClick(help)}
              className="group relative bg-white rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden"
            >
              {/* 중요 도움말 리본 */}
              {help.is_important && (
                <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-8 py-1 transform rotate-45 translate-x-6 translate-y-2 z-10">
                  중요
                </div>
              )}
              
              <div className="p-4 sm:p-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  {/* 아이콘 영역 */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className={`absolute inset-0 rounded-lg blur-lg opacity-20 group-hover:opacity-30 transition-opacity ${
                        help.is_important ? 'bg-red-500' : 'bg-blue-500'
                      }`}></div>
                      <div className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center shadow-md ${
                        help.is_important 
                          ? 'bg-gradient-to-br from-red-400 to-red-600' 
                          : 'bg-gradient-to-br from-blue-400 to-blue-600'
                      }`}>
                        {help.is_important ? (
                          <StarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        ) : (
                          <QuestionMarkCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* 컨텐츠 영역 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {help.category && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-lg bg-gray-100 text-gray-700">
                          {getCategoryLabel(help.category)}
                        </span>
                      )}
                      {help.target_audience && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-lg ${getTargetAudienceColor(help.target_audience)}`}>
                          {getTargetAudienceLabel(help.target_audience)}
                        </span>
                      )}
                    </div>
                    
                    <h3 className={`text-base sm:text-lg font-bold mb-1 transition-colors line-clamp-2 ${
                      help.is_important ? 'text-red-600 group-hover:text-red-700' : 'text-gray-900 group-hover:text-blue-600'
                    }`}>
                      {help.title}
                    </h3>
                    
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <EyeIcon className="h-3.5 w-3.5 text-gray-400" />
                        <span>조회 {help.view_count || 0}</span>
                      </div>
                      <span>{formatDate(help.created_at)}</span>
                      {help.attachments && help.attachments.length > 0 && (
                        <div className="flex items-center gap-1">
                          <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
                          <span>첨부 {help.attachments.length}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* 화살표 */}
                  <div className="hidden sm:flex flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronRightIcon className={`h-6 w-6 ${
                      help.is_important ? 'text-red-500' : 'text-blue-500'
                    }`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 페이지네이션 */}
        {helpData?.pages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-3 rounded-l-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="h-5 w-5 rotate-180" />
                <span className="ml-1">이전</span>
              </button>
              
              <div className="relative inline-flex items-center px-6 py-3 border-t border-b border-gray-300 bg-gray-50">
                <span className="text-sm text-gray-700">
                  페이지 <span className="font-bold text-blue-600">{page}</span> / <span className="font-medium">{helpData?.pages}</span>
                </span>
              </div>
              
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === helpData?.pages}
                className="relative inline-flex items-center px-4 py-3 rounded-r-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <span className="mr-1">다음</span>
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </nav>
          </div>
        )}

        {/* 도움말이 없을 때 */}
        {helpData?.helps?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="absolute inset-0 bg-gray-200 rounded-full blur-2xl opacity-30"></div>
              <div className="relative bg-white rounded-full p-8 shadow-lg">
                <QuestionMarkCircleIcon className="h-16 w-16 text-gray-400" />
              </div>
            </div>
            <h3 className="mt-6 text-lg font-semibold text-gray-900">도움말이 없습니다</h3>
            <p className="mt-2 text-sm text-gray-500">검색 조건을 변경해보세요.</p>
          </div>
        )}
      </div>

      {/* 도움말 상세 모달 */}
      {selectedHelp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] mb-16 flex flex-col overflow-hidden">
            {/* 헤더 */}
            <div className="flex-shrink-0 bg-gradient-to-r from-teal-50 to-cyan-50 px-4 md:px-6 py-5 border-b border-teal-100">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedHelp.is_important && (
                      <span className="inline-flex px-3 py-1 text-sm font-medium rounded-lg bg-red-100 text-red-700">
                        <StarIcon className="h-4 w-4 mr-1" />
                        중요
                      </span>
                    )}
                    {selectedHelp.category && (
                      <span className="inline-flex px-3 py-1 text-sm font-medium rounded-lg bg-gray-100 text-gray-700">
                        {getCategoryLabel(selectedHelp.category)}
                      </span>
                    )}
                    {selectedHelp.target_audience && (
                      <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-lg ${getTargetAudienceColor(selectedHelp.target_audience)}`}>
                        {getTargetAudienceLabel(selectedHelp.target_audience)}
                      </span>
                    )}
                  </div>
                  
                  <h2 className="text-lg font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent break-all min-w-0">
                    {selectedHelp.title}
                  </h2>
                  
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <EyeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <span className="font-medium">조회 {selectedHelp.view_count || 0}</span>
                    </div>
                    <span>{formatDate(selectedHelp.created_at)}</span>
                    {selectedHelp.author_name && (
                      <span className="text-gray-500">작성자: {selectedHelp.author_name}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedHelp(null)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-all -mr-1"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* 컨텐츠 */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-white to-gray-50/30">
              <div className="p-4 sm:p-6">
                <div className="prose prose-sm sm:prose max-w-none prose-pre:whitespace-pre-wrap prose-p:break-words prose-p:whitespace-pre-wrap">
                  <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    img: ({node, ...props}) => (
                      <div className="flex justify-center my-4">
                        <img {...props} className="max-w-full h-auto rounded-lg shadow-md" alt="" />
                      </div>
                    ),
                    a: ({node, ...props}) => (
                      <a {...props} className="text-blue-600 hover:text-blue-800 underline break-all" target="_blank" rel="noopener noreferrer" />
                    ),
                    p: ({node, ...props}) => (
                      <p {...props} className="mb-4 whitespace-pre-wrap break-words" />
                    ),
                    table: ({node, ...props}) => (
                      <div className="overflow-x-auto my-4">
                        <table {...props} className="min-w-full divide-y divide-gray-200" />
                      </div>
                    ),
                    thead: ({node, ...props}) => (
                      <thead {...props} className="bg-gray-50" />
                    ),
                    th: ({node, ...props}) => (
                      <th {...props} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    ),
                    td: ({node, ...props}) => (
                      <td {...props} className="px-6 py-4 whitespace-normal break-words text-sm text-gray-900" />
                    ),
                    blockquote: ({node, ...props}) => (
                      <blockquote {...props} className="pl-4 border-l-4 border-gray-300 text-gray-600 italic my-4" />
                    ),
                    code: ({node, inline, ...props}) => 
                      inline ? (
                        <code {...props} className="px-1 py-0.5 bg-gray-100 text-red-600 rounded text-sm break-all" />
                      ) : (
                        <code {...props} className="block p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto my-4 whitespace-pre-wrap" />
                      ),
                    pre: ({node, ...props}) => (
                      <pre {...props} className="my-4 whitespace-pre-wrap" />
                    ),
                  }}
                >
                  {selectedHelp.content}
                  </ReactMarkdown>
                </div>
                
                {/* 첨부파일 섹션 */}
                {selectedHelp.attachments && selectedHelp.attachments.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                      첨부파일 ({selectedHelp.attachments.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedHelp.attachments.map((file, index) => (
                        <a
                          key={index}
                          href={file.url}
                          download={file.name}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                        >
                          <div className="flex-shrink-0">
                            {file.type?.startsWith('image/') ? (
                              <PhotoIcon className="h-5 w-5 text-blue-500" />
                            ) : (
                              <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {file.size && `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                            </p>
                          </div>
                          <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* 푸터 - 고정 */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 md:px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setSelectedHelp(null)}
                className="w-full px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl font-medium"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Help;